import { useMemo } from 'react';
import { Group, Image as KonvaImage, Rect } from 'react-konva';
import { useStore } from '~/store';
import type { KeyedRegion } from '~/types/pipeline';
import { REGION_COLOR, REGION_RGB } from './colors';

function maskToCanvas(region: KeyedRegion): HTMLCanvasElement {
  const { bbox, mask } = region;
  const canvas = document.createElement('canvas');
  canvas.width = bbox.w;
  canvas.height = bbox.h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;
  const img = ctx.createImageData(bbox.w, bbox.h);
  for (let i = 0, p = 0; i < mask.length; i++, p += 4) {
    const m = mask[i] ?? 0;
    if (m === 0) continue;
    img.data[p] = REGION_RGB[0];
    img.data[p + 1] = REGION_RGB[1];
    img.data[p + 2] = REGION_RGB[2];
    img.data[p + 3] = m;
  }
  ctx.putImageData(img, 0, 0);
  return canvas;
}

type RegionNodeProps = {
  region: KeyedRegion;
  alpha: number;
  stroke: 'none' | 'dashed';
  onMouseEnter: () => void;
  onMouseLeave: () => void;
};

function RegionNode({ region, alpha, stroke, onMouseEnter, onMouseLeave }: RegionNodeProps) {
  const canvas = useMemo(() => maskToCanvas(region), [region]);
  const { bbox } = region;
  return (
    <Group x={bbox.x} y={bbox.y}>
      <KonvaImage
        image={canvas}
        width={bbox.w}
        height={bbox.h}
        opacity={alpha}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      />
      {stroke === 'dashed' && (
        <Rect
          width={bbox.w}
          height={bbox.h}
          stroke={REGION_COLOR}
          strokeWidth={1}
          strokeScaleEnabled={false}
          listening={false}
          dash={[4, 3]}
        />
      )}
    </Group>
  );
}

/** Konva layer rendering all keyed regions as tinted overlays. */
export function RegionLayer() {
  const regions = useStore((s) => s.background.regions);
  const drawMode = useStore((s) => s.editor.drawMode);
  const showRegions = useStore((s) => s.editor.showRegions);
  const hoveredId = useStore((s) => s.editor.hoveredRegionId);
  const selectedId = useStore((s) => s.editor.selectedRegionId);
  const setHoveredRegionId = useStore((s) => s.setHoveredRegionId);

  const editing = drawMode === 'keyRegion';
  if (!editing && !showRegions) return null;
  if (regions.length === 0) return null;

  return (
    <>
      {regions.map((region) => {
        const isSelected = region.id === selectedId;
        const isHovered = region.id === hoveredId;
        const alpha = editing ? (isSelected ? 0.7 : isHovered ? 0.55 : 0.35) : 0.2;
        const stroke: 'none' | 'dashed' = editing && isSelected ? 'dashed' : 'none';
        return (
          <RegionNode
            key={region.id}
            region={region}
            alpha={alpha}
            stroke={stroke}
            onMouseEnter={() => setHoveredRegionId(region.id)}
            onMouseLeave={() => setHoveredRegionId(null)}
          />
        );
      })}
    </>
  );
}
